// THE LOOP - Main entry point

// Mobile nav toggle — event delegation (survives shell swaps)
document.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('.nav-toggle');
    if (!btn) return;
    const nav = document.getElementById('site-nav');
    if (!nav) return;
    const isOpen = nav.classList.toggle('site-nav--open');
    btn.classList.toggle('nav-toggle--active', isOpen);
    btn.setAttribute('aria-expanded', String(isOpen));
});

// High contrast toggle — event delegation, persisted in localStorage
const CONTRAST_KEY = 'theloop-high-contrast';
if (localStorage.getItem(CONTRAST_KEY) === '1') {
    document.documentElement.setAttribute('data-contrast', 'high');
}

document.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('.contrast-toggle');
    if (!btn) return;
    const isHigh = document.documentElement.getAttribute('data-contrast') === 'high';
    if (isHigh) {
        document.documentElement.removeAttribute('data-contrast');
        btn.setAttribute('aria-pressed', 'false');
        localStorage.removeItem(CONTRAST_KEY);
    } else {
        document.documentElement.setAttribute('data-contrast', 'high');
        btn.setAttribute('aria-pressed', 'true');
        localStorage.setItem(CONTRAST_KEY, '1');
    }
});

// Onboarding — first-visit loading sequence (must run before scroll reveal)
import { initOnboarding } from './onboarding';
initOnboarding();

// Scroll reveal animations
import { initScrollReveal } from './scroll-reveal';
initScrollReveal();

// 3D Background — WebGPURenderer with auto WebGPU/WebGL2 fallback
import { detectCapabilities } from './scene/detect-capabilities';
import { createTronScene } from './scene/create-scene';

const caps = detectCapabilities();
if (caps.webgl2) {
    const tronScene = createTronScene(caps);
    tronScene.start();
}
// No WebGL2: .grid-bg CSS fallback is already visible

// Music player + volume control — replaces the ambient drone
import { createMusicPlayer } from './audio/music-player';
import { createVolumeControl } from './audio/volume-control';

const musicPlayer = createMusicPlayer();
if (musicPlayer) {
    createVolumeControl(musicPlayer);
}

// Contact form — challenge + PoW + async submission
import { initContactForm } from './contact-form';
initContactForm();

// HTMX navigation handlers

// After every HTMX swap (navigation or language switch)
document.body.addEventListener('htmx:afterSettle', (e: Event) => {
    const detail = (e as CustomEvent).detail;
    const target = detail.elt as HTMLElement;

    // 1. Update lang and description from hidden metadata div
    //    (title is handled natively by htmx via the <title> tag in the response)
    const meta = target.querySelector('#htmx-meta') as HTMLElement | null;
    if (meta) {
        const lang = meta.dataset.lang || '';
        document.documentElement.lang = lang;
        document.documentElement.dataset.lang = lang;
        document.querySelector('meta[name="description"]')
            ?.setAttribute('content', meta.dataset.desc || '');
    }

    // 2. Re-initialize content-dependent modules
    initScrollReveal();
    initContactForm();

    // 3. Close mobile nav if open
    const nav = document.getElementById('site-nav');
    const toggle = document.querySelector<HTMLButtonElement>('.nav-toggle');
    if (nav?.classList.contains('site-nav--open')) {
        nav.classList.remove('site-nav--open');
        toggle?.classList.remove('nav-toggle--active');
        toggle?.setAttribute('aria-expanded', 'false');
    }

    // 4. Restore contrast toggle state after shell swap
    if (target.id === 'app-shell') {
        const btn = document.querySelector<HTMLButtonElement>('.contrast-toggle');
        if (btn && localStorage.getItem(CONTRAST_KEY) === '1') {
            btn.setAttribute('aria-pressed', 'true');
        }
    }
});

// Language switcher — override URL at request time so it always reflects the current page.
// The template renders hx-get="/en{{.PagePath}}" which becomes stale after same-language nav.
// htmx:configRequest fires before the request, letting us patch detail.path dynamically.
document.body.addEventListener('htmx:configRequest', (e: Event) => {
    const detail = (e as CustomEvent).detail;
    const elt = detail.elt as HTMLElement;
    if (!elt.classList.contains('site-nav__lang')) return;
    const pagePath = window.location.pathname.replace(/^\/(fr|en)/, '') || '/';
    const targetLang = elt.textContent?.trim() === 'EN' ? '/en' : '/fr';
    detail.path = targetLang + pagePath;
});

// Active nav link — update underline after same-language navigation.
// Shell swap (language change) updates the nav server-side, but content-only swap doesn't.
document.body.addEventListener('htmx:pushedIntoHistory', () => {
    const path = window.location.pathname.replace(/^\/(fr|en)/, '') || '/';
    document.querySelectorAll<HTMLAnchorElement>('.site-nav__link').forEach((link) => {
        const linkPath = new URL(link.href).pathname.replace(/^\/(fr|en)/, '') || '/';
        link.classList.toggle('site-nav__link--active', linkPath === path);
    });
});

// History restore (browser back/forward from cache)
document.body.addEventListener('htmx:historyRestore', () => {
    initScrollReveal();
    initContactForm();
});
